/**
 * Category Service
 * 
 * Handles all category-related API calls.
 * 
 * @validates Requirements 5.1-5.6 - Category management
 */

import api from './api';
import {
  Category,
  CategoryTreeNode,
  CreateCategoryRequest,
  UpdateCategoryRequest,
  ApiResponse,
} from '@budgira/shared';

export const categoryService = {
  /**
   * Get all categories as a tree structure
   */
  async getTree(): Promise<ApiResponse<CategoryTreeNode[]>> {
    const response = await api.get<ApiResponse<CategoryTreeNode[]>>('/categories');
    return response.data;
  },

  /**
   * Get a specific category by ID
   */
  async getById(id: string): Promise<ApiResponse<Category>> {
    const response = await api.get<ApiResponse<Category>>(`/categories/${id}`);
    return response.data;
  },

  /**
   * Get children of a category
   */
  async getChildren(id: string): Promise<ApiResponse<Category[]>> {
    const response = await api.get<ApiResponse<Category[]>>(`/categories/${id}/children`);
    return response.data;
  },

  /**
   * Create a new category
   */
  async create(data: CreateCategoryRequest): Promise<ApiResponse<Category>> {
    const response = await api.post<ApiResponse<Category>>('/categories', data);
    return response.data;
  },

  /**
   * Update a category
   */
  async update(id: string, data: UpdateCategoryRequest): Promise<ApiResponse<Category>> {
    const response = await api.put<ApiResponse<Category>>(`/categories/${id}`, data);
    return response.data;
  },

  /**
   * Delete a category
   */
  async delete(id: string): Promise<ApiResponse<null>> {
    const response = await api.delete<ApiResponse<null>>(`/categories/${id}`);
    return response.data;
  },

  /**
   * Upload/update category logo
   */
  async updateLogo(id: string, logoUrl: string): Promise<ApiResponse<Category>> {
    const response = await api.post<ApiResponse<Category>>(`/categories/${id}/logo`, { logoUrl });
    return response.data;
  },

  /**
   * Add all available templates to user's categories
   */
  async addAllTemplates(): Promise<ApiResponse<{ count: number }>> {
    const response = await api.post<ApiResponse<{ count: number }>>('/categories/templates/add-all');
    return response.data;
  },

  /**
   * Reset categories to templates (re-add template categories without touching personal ones)
   */
  async resetToTemplates(): Promise<ApiResponse<{ deleted: number; added: number }>> {
    const response = await api.post<ApiResponse<{ deleted: number; added: number }>>('/categories/templates/reset');
    return response.data;
  },

  /**
   * Remove all template-based categories (keeps personal categories)
   */
  async removeTemplate(): Promise<ApiResponse<{ count: number }>> {
    const response = await api.post<ApiResponse<{ count: number }>>('/categories/templates/remove');
    return response.data;
  },

  /**
   * Purge unused personal categories (categories without templateId not used in operations/schedulers)
   */
  async purgeUnused(): Promise<ApiResponse<{ count: number }>> {
    const response = await api.post<ApiResponse<{ count: number }>>('/categories/purge-unused');
    return response.data;
  },
};

export default categoryService;
